import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { dataRoomService } from '../services/dataRoomService';
import { formatFileSize } from '../types/dataRoom';
import {
  FiFolder,
  FiFile,
  FiAlertCircle,
  FiChevronDown,
  FiFileText,
  FiEye,
} from 'react-icons/fi';
import DocumentViewerModal from '../components/datarooms/DocumentViewerModal';
import FileTypeIcon from '../components/datarooms/FileTypeIcon';

const FolderIcon = FiFolder as React.FC<React.SVGProps<SVGSVGElement>>;
const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;
const ChevronDownIcon = FiChevronDown as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;

interface PublicDocument {
  id: string;
  name: string;
  file_type: string;
  file_size: number;
  original_filename: string;
  // The API no longer returns a download URL for public docs — it returns
  // a `viewable: true` flag and the frontend streams bytes on demand from
  // /api/public/data-room/.../documents/:id/stream so the visitor's
  // browser never sees a Supabase signed URL.
  viewable: boolean;
}

interface PublicFolder {
  id: string;
  name: string;
  position: number;
  parent_folder_id?: string | null;
  documents: PublicDocument[];
  subfolders?: PublicFolder[];
}

interface PublicDataRoomData {
  data_room: {
    id: string;
    name: string;
    description: string | null;
    entry_title: string | null;
    entry_content: string | null;
    allow_downloads: boolean;
    require_email: boolean;
  };
  folders: PublicFolder[];
  share_link_id: string;
}

const PublicDataRoomView: React.FC = () => {
  // Both old (/room/:token) and new (/data-room/:businessSlug/:linkSlug) routes render this component.
  // The presence of linkSlug indicates the new slug-based format.
  const { token, linkSlug } = useParams<{ token?: string; linkSlug?: string }>();
  const isSlugBased = !!linkSlug;
  const identifier = linkSlug || token;

  const [roomData, setRoomData] = useState<PublicDataRoomData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [viewingDoc, setViewingDoc] = useState<PublicDocument | null>(null);
  const hasRecordedView = useRef(false);

  const loadDataRoom = useCallback(async () => {
    if (!identifier) return;
    try {
      setLoading(true);
      setError(null);
      const data = isSlugBased
        ? await dataRoomService.getPublicDataRoomBySlug(identifier)
        : await dataRoomService.getPublicDataRoom(identifier);
      setRoomData(data);
      // Expand all folders and sub-folders by default — visitors expect to
      // see everything they have access to without having to click around.
      const allFolderIds = new Set<string>();
      data.folders.forEach((f: PublicFolder) => {
        allFolderIds.add(f.id);
        if (f.subfolders) {
          f.subfolders.forEach((sf: PublicFolder) => allFolderIds.add(sf.id));
        }
      });
      setExpandedFolders(allFolderIds);
    } catch (err: any) {
      console.error('Failed to load data room:', err);
      setError(err?.message || 'This data room is not available or the link has expired.');
    } finally {
      setLoading(false);
    }
  }, [identifier, isSlugBased]);

  const recordView = useCallback(async () => {
    if (!identifier || hasRecordedView.current) return;
    hasRecordedView.current = true;
    try {
      if (isSlugBased) {
        await dataRoomService.recordPublicViewBySlug(identifier);
      } else {
        await dataRoomService.recordPublicView(identifier);
      }
    } catch (err) {
      console.error('Failed to record view:', err);
    }
  }, [identifier, isSlugBased]);

  useEffect(() => {
    loadDataRoom();
  }, [loadDataRoom]);

  useEffect(() => {
    if (roomData) {
      const timer = setTimeout(recordView, 3000);
      return () => clearTimeout(timer);
    }
  }, [roomData, recordView]);

  // Block right-click and print shortcuts on the public data room.
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    const blockPrint = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', blockPrint);
    return () => document.removeEventListener('keydown', blockPrint);
  }, []);

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  // Loading state — same dark page chrome as the rest of the app.
  if (loading) {
    return (
      <div style={pageWrapperStyle}>
        <div style={emptyStateStyle}>
          <div className="su-spinner" />
          <p style={{ color: 'var(--su-text-muted)', marginTop: 16, fontSize: 14 }}>
            Loading data room…
          </p>
        </div>
      </div>
    );
  }

  if (error || !roomData) {
    return (
      <div style={pageWrapperStyle}>
        <div style={emptyStateStyle}>
          <AlertIcon style={{ width: 56, height: 56, color: 'var(--su-text-subtle)', marginBottom: 16 }} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--su-text-primary)', margin: 0 }}>
            Data Room Not Available
          </h1>
          <p style={{ color: 'var(--su-text-muted)', fontSize: 14, marginTop: 8, maxWidth: 420 }}>
            {error || 'This data room could not be found.'}
          </p>
          <p style={{ color: 'var(--su-text-subtle)', fontSize: 12, marginTop: 8 }}>
            The link may have expired or been deactivated by the owner.
          </p>
        </div>
      </div>
    );
  }

  const { data_room, folders } = roomData;
  const totalDocs = folders.reduce((sum, f) => {
    let count = f.documents.length;
    if (f.subfolders) {
      count += f.subfolders.reduce((s, sf) => s + sf.documents.length, 0);
    }
    return sum + count;
  }, 0);
  const totalFolders = folders.reduce((sum, f) => sum + 1 + (f.subfolders?.length || 0), 0);

  const renderDocumentRow = (doc: PublicDocument) => (
    <div
      key={doc.id}
      className="su-document-row"
      onClick={() => doc.viewable && setViewingDoc(doc)}
      style={{ cursor: doc.viewable ? 'pointer' : 'default' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: 'transparent',
        }}
      >
        <FileTypeIcon
          fileType={doc.file_type}
          fallbackFilename={doc.original_filename}
          size={32}
        />
      </div>
      <div className="su-document-info">
        <div className="su-document-name" title={doc.name}>{doc.name}</div>
        <div className="su-document-meta">{formatFileSize(doc.file_size)}</div>
      </div>
      {doc.viewable && (
        <div className="su-document-actions" style={{ opacity: 1 }}>
          <button
            className="su-document-view"
            onClick={(e) => {
              e.stopPropagation();
              setViewingDoc(doc);
            }}
            title="View document"
            aria-label="View document"
          >
            <EyeIcon width={14} height={14} />
          </button>
        </div>
      )}
    </div>
  );

  const renderFolder = (folder: PublicFolder, isSubfolder = false) => {
    const isExpanded = expandedFolders.has(folder.id);
    const hasContent = folder.documents.length > 0 || (folder.subfolders && folder.subfolders.length > 0);
    return (
      <div
        key={folder.id}
        className="su-folder"
        style={isSubfolder ? { marginLeft: 24, borderLeft: '2px solid var(--su-border-primary)' } : undefined}
      >
        <div
          className="su-folder-header"
          onClick={() => toggleFolder(folder.id)}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
        >
          <div className="su-folder-info">
            <ChevronDownIcon
              className="su-folder-icon"
              style={{
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
              }}
            />
            <FolderIcon className="su-folder-icon" />
            <span className="su-folder-name">{folder.name}</span>
            <span className="su-folder-count">
              {folder.documents.length} {folder.documents.length === 1 ? 'file' : 'files'}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="su-folder-content">
            {folder.documents.length === 0 && (!folder.subfolders || folder.subfolders.length === 0) ? (
              <div className="su-folder-empty">
                <FileIcon style={{ width: 14, height: 14, marginRight: 6, verticalAlign: 'middle' }} />
                No documents in this folder
              </div>
            ) : (
              <>
                {folder.documents.length > 0 && (
                  <div className="su-document-list">
                    {folder.documents.map(renderDocumentRow)}
                  </div>
                )}
                {folder.subfolders && folder.subfolders.length > 0 && (
                  <div style={{ paddingTop: folder.documents.length > 0 ? 8 : 0 }}>
                    {folder.subfolders.map((sf) => renderFolder(sf, true))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={pageWrapperStyle} onContextMenu={handleContextMenu}>
      {/* Top brand strip — replaces the missing app navbar so visitors
          have a consistent header even though they're not logged in. */}
      <div style={topBarStyle}>
        <div style={topBarInnerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--su-purple-accent)', fontWeight: 700, fontSize: 18 }}>
              sendupdates.ai
            </span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--su-text-subtle)' }}>
            Secure Data Room
          </span>
        </div>
      </div>

      {/* Page body — same width and rhythm as the owner detail page. */}
      <div style={pageBodyStyle}>
        <div className="su-dataroom-detail">
          {/* Header — mirrors .su-dataroom-detail-header from the owner side */}
          <div className="su-dataroom-detail-header">
            <div className="su-dataroom-detail-title">
              <div>
                <h1 className="su-dataroom-name">{data_room.name}</h1>
                {data_room.description && (
                  <p className="su-dataroom-subtitle">{data_room.description}</p>
                )}
                <p
                  style={{
                    fontSize: 12,
                    color: 'var(--su-text-subtle)',
                    marginTop: 6,
                  }}
                >
                  {totalFolders} folder{totalFolders === 1 ? '' : 's'} · {totalDocs} document
                  {totalDocs === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          </div>

          {/* Entry Page section — same card chrome as owner */}
          {(data_room.entry_title || data_room.entry_content) && (
            <div className="su-dataroom-section">
              <div className="su-dataroom-section-header">
                <div className="su-dataroom-section-title">
                  <FileTextIcon />
                  <span>{data_room.entry_title || 'Welcome'}</span>
                </div>
              </div>
              {data_room.entry_content && (
                <div
                  className="su-dataroom-section-body"
                  style={{
                    fontSize: 14,
                    color: 'var(--su-text-muted)',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {data_room.entry_content}
                </div>
              )}
            </div>
          )}

          {/* Folders & Documents — same card + folder shapes as owner */}
          <div className="su-dataroom-section">
            <div className="su-dataroom-section-header">
              <div className="su-dataroom-section-title">
                <FolderIcon />
                <span>Folders &amp; Documents</span>
              </div>
            </div>
            <div className="su-dataroom-section-body">
              {folders.length > 0 ? (
                <div className="su-folders-list">{folders.map((f) => renderFolder(f))}</div>
              ) : (
                <div className="su-folder-empty">No documents have been shared yet.</div>
              )}
            </div>
          </div>

          {/* Footer — minimal brand line */}
          <div
            style={{
              textAlign: 'center',
              padding: '24px 0 8px',
              fontSize: 12,
              color: 'var(--su-text-subtle)',
            }}
          >
            Shared via{' '}
            <a
              href="https://sendupdates.ai"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--su-purple-accent)' }}
            >
              SendUpdates.ai
            </a>
          </div>
        </div>
      </div>

      {viewingDoc && identifier && (
        <DocumentViewerModal
          doc={{
            id: viewingDoc.id,
            name: viewingDoc.name,
            file_type: viewingDoc.file_type,
            file_size: viewingDoc.file_size,
            original_filename: viewingDoc.original_filename,
          }}
          fetchBytes={() =>
            isSlugBased
              ? dataRoomService.fetchPublicDocumentBytesBySlug(identifier, viewingDoc.id)
              : dataRoomService.fetchPublicDocumentBytesByToken(identifier, viewingDoc.id)
          }
          fetchOfficeViewerUrl={() =>
            isSlugBased
              ? dataRoomService.getPublicOfficeViewerUrlBySlug(identifier, viewingDoc.id)
              : dataRoomService.getPublicOfficeViewerUrlByToken(identifier, viewingDoc.id)
          }
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
};

// Public-page-only chrome. The owner page lives inside <Layout> which
// provides background + nav; this page renders standalone so we supply
// equivalent chrome inline using the same design tokens.
const pageWrapperStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--su-color-black)',
  color: 'var(--su-text-primary)',
  display: 'flex',
  flexDirection: 'column',
  // Hint at the design's no-text-selection stance for view-only files —
  // matches the modal which already disables selection.
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

const topBarStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--su-border-primary)',
  background: 'var(--su-gray-900)',
};

const topBarInnerStyle: React.CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: '14px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const pageBodyStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  maxWidth: 1100,
  margin: '0 auto',
  padding: '32px 24px 48px',
  boxSizing: 'border-box',
};

const emptyStateStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '64px 24px',
  textAlign: 'center',
};

export default PublicDataRoomView;
